import React, {useEffect, useMemo, useState} from "react";
import {Autocomplete, Dialog, DialogActions, DialogContent, DialogTitle, TextField, Typography} from "@mui/material";
import {useNavigate} from "react-router";
import {makeCresAxiosFoundationAPI} from "../../util/AxiosFoundationAPI";
import {useSessionContext} from "../../util/SessionContext";
import CCButton from "../Common/CCButton";
import css_buttons from "../Common/css/CCButton.module.scss";
import {Grey7, Grey17} from "../../theme/styles";
import {listOf} from "../../util/projectMembers";

/**
 * Opening a project: who it is for, and what it is.
 *
 * ED, 2026-10-29: "We need to capture Customer Company, Customer Name and Customer Email when
 * creating a new Project. Name and Description (Optional) also." Five fields, four of them
 * required, and the generic create dialog could ask none of the customer ones -- it asks a type, a
 * number and an owner, which a project decides for itself (one type by design, the number from the
 * pool, the owner the persona that opens it). So a project has its own dialog, and it posts to the
 * project's own door, /t1/v1/projects, which takes the customer trio together.
 *
 * THE COMPANY IS A CUSTOMER RECORD. The field offers the account's customers by name and takes a
 * name it has not heard of: the service finds the record of that name or creates one, so a typed
 * "Acme Corp" and a picked "Acme Corp" land on the same record. The contact's name and address
 * are the folder's own facts and travel with it.
 *
 * THE SERVER IS THE ONE THAT REFUSES. The Create button waits for the four required fields and
 * an address with an @ in it, which is a courtesy; the trio-together rule and the email shape are
 * enforced by Create Project (CC-PROJ-CUST, CC-PROJ-EMAIL), and its sentence is what is shown.
 */
type Props = {
    open: boolean;
    onClose: () => void;
    onCreated?: (created: {ObjectUSID?: string; RevisionUSID?: string; ObjectID?: string}) => void;
    route: string;
};

type CustomerOption = {ObjectUSID: string; Name: string; ObjectID?: string | null};

const LOOKS_LIKE_AN_ADDRESS = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

export default function CreateProject({open, onClose, onCreated, route}: Props) {
    const navigate = useNavigate();
    const sessionContext = useSessionContext();
    const api = useMemo(() => makeCresAxiosFoundationAPI(sessionContext), [sessionContext]);

    const [name, setName] = useState("");
    const [description, setDescription] = useState("");
    const [company, setCompany] = useState("");
    const [chosen, setChosen] = useState<CustomerOption | null>(null);
    const [contactName, setContactName] = useState("");
    const [contactEmail, setContactEmail] = useState("");
    const [customers, setCustomers] = useState<CustomerOption[]>([]);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const headers = () => {
        const active = sessionContext?.getActiveVisibility();
        return {"visibility-mode": active?.Mode ?? "", "visibility-value": active?.Value ?? ""};
    };

    // THE ACCOUNT'S CUSTOMERS, BY NAME, for the company field to offer. Read when the dialog
    // opens, not when the page does: most visits to the list never open it.
    useEffect(() => {
        if (!open) return;
        setName(""); setDescription(""); setCompany(""); setChosen(null);
        setContactName(""); setContactEmail(""); setError(null); setSaving(false);
        let live = true;
        api.get("t1/v1/object_revisions",
                {params: {ObjectClassification: "Customer", IsCurrent: "true", Limit: 300, Sort: "Name:asc"},
                 headers: {"idoc-response": "v1.ObjectRevision-001", ...headers()}})
            .then((response: any) => {
                const rows = listOf<any>(response?.data?.PlatformMessage?.ListOfObjectRevision?.ObjectRevision);
                if (live) setCustomers(rows.filter(one => one?.ObjectUSID && one?.Name)
                                           .map(one => ({ObjectUSID: String(one.ObjectUSID), Name: String(one.Name),
                                                         ObjectID: one.ObjectID ?? null})));
            })
            .catch(() => { if (live) setCustomers([]); });
        return () => { live = false; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, api]);

    const canSave = !saving && name.trim().length > 0 && company.trim().length > 0
        && contactName.trim().length > 0 && LOOKS_LIKE_AN_ADDRESS.test(contactEmail.trim());

    const save = async () => {
        setSaving(true);
        setError(null);
        try {
            const body: any = {
                Name: name.trim(),
                Description: description.trim() || undefined,
                CustomerName: contactName.trim(),
                CustomerEmail: contactEmail.trim(),
            };
            // A PICKED CUSTOMER GOES BY RECORD, a typed one by name -- unless the typed name is a
            // customer's exact name, which is the picked case by another route.
            const typed = company.trim();
            const match = chosen && chosen.Name === typed ? chosen
                        : customers.find(one => one.Name.trim().toLowerCase() === typed.toLowerCase()) ?? null;
            if (match) body.CustomerObjectUSID = match.ObjectUSID; else body.CustomerCompany = typed;

            const response = await api.post("t1/v1/projects", body, {headers: headers()});
            const made = listOf<any>(response?.data?.PlatformMessage?.ListOfProjectRevision?.ProjectRevision)[0]
                      ?? response?.data?.PlatformMessage?.ProjectRevision;
            onCreated?.({ObjectUSID: made?.ObjectUSID, RevisionUSID: made?.USID, ObjectID: made?.ObjectID});
            onClose();
            if (made?.USID) navigate(`${route}/${made.USID}`);
        } catch (e: any) {
            const message = e?.response?.data?.Cause || e?.response?.data?.Message;
            setError(message || "We could not open this project. Please try again.");
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="sm" fullWidth>
            <DialogTitle sx={{fontSize: 18, fontWeight: 500, color: Grey17}}>New project</DialogTitle>
            <DialogContent>
                <div style={{display: "flex", flexDirection: "column", gap: 18, paddingTop: 8}}>
                    <TextField label="Name" size="small" value={name} onChange={e => setName(e.target.value)}
                               fullWidth autoFocus required/>
                    <TextField label="Description" size="small" value={description}
                               onChange={e => setDescription(e.target.value)} fullWidth multiline minRows={2}/>

                    {/* WHO IT IS FOR. Three fields under one heading, because the server takes them
                        as one fact: a company with nobody to write to is refused. */}
                    <Typography style={{fontSize: 13, color: Grey7, marginTop: 4}}>Customer</Typography>
                    <Autocomplete
                        freeSolo
                        options={customers}
                        getOptionLabel={option => typeof option === "string" ? option : option.Name}
                        value={chosen}
                        inputValue={company}
                        onInputChange={(_, value) => { setCompany(value); if (chosen && value !== chosen.Name) setChosen(null); }}
                        onChange={(_, value) => {
                            if (value && typeof value !== "string") { setChosen(value); setCompany(value.Name); }
                            else if (typeof value === "string") { setChosen(null); setCompany(value); }
                            else setChosen(null);
                        }}
                        renderOption={(props, option) => (
                            <li {...props} key={option.ObjectUSID}>
                                <span>{option.Name}</span>
                                {option.ObjectID && <span style={{color: Grey7, marginLeft: 8, fontSize: 12}}>{option.ObjectID}</span>}
                            </li>
                        )}
                        renderInput={params => (
                            <TextField {...params} label="Customer Company" size="small" required
                                       helperText={company.trim() && !customers.some(one => one.Name.trim().toLowerCase() === company.trim().toLowerCase())
                                           ? "Not a customer on record yet; one will be created with this name." : " "}/>
                        )}
                    />
                    <TextField label="Customer Name" size="small" value={contactName}
                               onChange={e => setContactName(e.target.value)} fullWidth required/>
                    <TextField label="Customer Email" size="small" type="email" value={contactEmail}
                               onChange={e => setContactEmail(e.target.value)} fullWidth required
                               error={contactEmail.trim().length > 0 && !LOOKS_LIKE_AN_ADDRESS.test(contactEmail.trim())}/>

                    {error && (
                        <Typography style={{fontSize: 13, color: "#B3261E"}}>{error}</Typography>
                    )}
                </div>
            </DialogContent>
            <DialogActions sx={{px: 3, pb: 2}}>
                <CCButton className={css_buttons.whiteStandardButton} onClick={onClose} disabled={saving}>Cancel</CCButton>
                <CCButton className={css_buttons.blueStandardButton} onClick={save} disabled={!canSave}>
                    {saving ? "Creating…" : "Create"}
                </CCButton>
            </DialogActions>
        </Dialog>
    );
}
